/*
BoxFlower is a custom component that displays the details of a flower product in a box format. It is used in the Provenance module to show the details of rose flower exports from Latin America. 

The component receives a record prop and displays the name/variety, producer/farm, country/region of origin, product ID, batch/lot number, SKU, stem length, color, grade/quality, bloom stage, head size, certifications, growing conditions, and description of the flower. The component uses the Box, Typography, and Chip components from the Material-UI library to display the details. 

The Box component is used to create a box layout for the flower details. The Typography component is used to display the title and description of the flower. The Chip components are used to display the stem length, color, grade/quality, bloom stage, and certifications.

The component is used in the ProvenanceList component in the Provenance module to display the details of a flower record.
*/

import React from 'react';
import { useTheme, Typography, Chip, Box } from '@mui/material';
import { TextInput, SelectInput } from 'react-admin';
import { required } from 'ra-core';

/**
 * Flower product box component
 * @component
 * @example
 * // Example record structure:
 * const flowerRecord = {
 *   id: "F123",
 *   name: "Red Freedom",
 *   producer: "Floresta Farms",
 *   origin: "Ecuador, Cayambe",
 *   stemLength: "60cm",
 *   color: "Red",
 *   grade: "Premium",
 *   bloomStage: "Semi-open",
 *   headSize: "5.5 cm",
 *   certifications: ["Rainforest Alliance", "Fair Trade"],
 *   growingConditions: "Greenhouse",
 *   batchNumber: "BL456",
 *   sku: "SKU789",
 *   description: "Long-lasting premium rose with vibrant color and excellent vase life...",
 *   image: "url/to/flower/image.jpg"
 * };
 */

export const defaultFlowerImage = "https://placehold.co/200x300?text=Flower&font=playfair-display";

const BoxFlower = ({ record }) => {
    const theme = useTheme();
    return (
        <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                {record.name || 'Unnamed Flower'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Producer/Farm:</strong> {record?.producer || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Origin:</strong> {record?.origin || 'Unknown'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip 
                    label={record?.stemLength || 'Length N/A'} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                />
                <Chip 
                    label={record.color || 'Color N/A'} 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                />
                <Chip 
                    label={record.grade || 'Grade N/A'} 
                    size="small" 
                />
                <Chip 
                    label={record.bloomStage || 'Bloom Stage N/A'} 
                    size="small" 
                    variant="outlined" 
                />
                {record.certifications && (
                    <Chip 
                        label={record.certifications} 
                        size="small" 
                    />
                )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Head Size:</strong> {record.headSize || 'Not specified'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Growing Conditions:</strong> {record.growingConditions || 'Not specified'}
            </Typography>
            {/* <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {record.certifications && (
                    <Chip 
                        label={record.certifications} 
                        size="small" 
                    />
                )}
            </Box> */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Typography variant="body2"><strong>Product ID:</strong> {record.id || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Batch/Lot Number:</strong> {record?.batchNumber || 'N/A'}</Typography>
                <Typography variant="body2"><strong>SKU:</strong> {record?.sku || 'N/A'}</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <strong>Description:</strong> {record?.description || 'No description available'}
                </Typography>
            </Box>
        </Box>
    );
};

export const renderFlowerFields = () => {
    return (
        <>
            <TextInput label="Flower Name/Variety" source="data.name" validate={required()} />
            <TextInput label="Producer/Farm" source="data.producer" validate={required()} />
            <TextInput label="Origin" source="data.origin" validate={required()} />
            <SelectInput 
                label="Stem Length" 
                source="data.stemLength" 
                choices={[
                    { id: '40cm', name: '40cm' },
                    { id: '50cm', name: '50cm' },
                    { id: '60cm', name: '60cm' },
                    { id: '70cm', name: '70cm' },
                    { id: '80cm', name: '80cm' },
                    { id: '90cm', name: '90cm' },
                    { id: '100cm', name: '100cm' }
                ]} 
                validate={required()} 
            />
            <SelectInput 
                label="Color" 
                source="data.color" 
                choices={[
                    { id: 'Red', name: 'Red' },
                    { id: 'White', name: 'White' },
                    { id: 'Pink', name: 'Pink' },
                    { id: 'Yellow', name: 'Yellow' },
                    { id: 'Orange', name: 'Orange' },
                    { id: 'Purple', name: 'Purple' },
                    { id: 'Peach', name: 'Peach' },
                    { id: 'Bicolor', name: 'Bicolor' }
                ]} 
                validate={required()} 
            />
            <SelectInput 
                label="Grade/Quality" 
                source="data.grade" 
                choices={[
                    { id: 'Premium', name: 'Premium' },
                    { id: 'Standard', name: 'Standard' },
                    { id: 'Commercial', name: 'Commercial' }
                ]} 
            />
            <SelectInput 
                label="Bloom Stage" 
                source="data.bloomStage" 
                choices={[
                    { id: 'Tight bud', name: 'Tight bud' },
                    { id: 'Semi-open', name: 'Semi-open' },
                    { id: 'Open', name: 'Open' }
                ]} 
            />
            <TextInput label="Head Size" source="data.headSize" />
            <SelectInput 
                label="Certifications" 
                source="data.certifications" 
                choices={[
                    { id: 'Rainforest Alliance', name: 'Rainforest Alliance' },
                    { id: 'Fair Trade', name: 'Fair Trade' },
                    { id: 'Veriflora', name: 'Veriflora' },
                    { id: 'Florverde', name: 'Florverde' },
                    { id: 'GlobalGAP', name: 'GlobalGAP' }
                ]}
                multiple
            />
            <SelectInput 
                label="Growing Conditions" 
                source="data.growingConditions" 
                choices={[
                    { id: 'Greenhouse', name: 'Greenhouse' },
                    { id: 'Field', name: 'Field' },
                    { id: 'Hydroponic', name: 'Hydroponic' }
                ]} 
            />
            <TextInput label="Batch/Lot #" source="data.batchNumber" validate={required()} />
            <TextInput label="SKU" source="data.sku" validate={required()} />
            <TextInput label="Price" source="data.price" validate={required()} />
            <TextInput label="Description" multiline source="data.description" />
        </>
    );
};

export default BoxFlower;